import { useCallback, useEffect, useState } from 'react';

export type GalleryFolder = { slug: string; label: string; itemCount?: number };
export type GalleryItem = {
  id: string;
  code: string;
  name: string;
  artworkUrl: string;
  garmentBias: string;
  folderSlug?: string;
};

/** Shared gallery catalog UI (desktop panel + mobile sheet). */
export function GalleryBrowser({
  apiBase,
  storeId = 'ox',
  onPickArtwork,
}: {
  apiBase: string;
  storeId?: string;
  onPickArtwork: (item: GalleryItem) => Promise<void>;
}) {
  const [folders, setFolders] = useState<GalleryFolder[]>([]);
  const [folder, setFolder] = useState('');
  const [q, setQ] = useState('');
  const [items, setItems] = useState<GalleryItem[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [picking, setPicking] = useState<string | null>(null);

  const base = apiBase.replace(/\/$/, '');

  const loadFolders = useCallback(async () => {
    const res = await fetch(`${base}/gallery/folders?storeId=${encodeURIComponent(storeId)}`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || 'folders_error');
    setFolders(data.items ?? []);
  }, [base, storeId]);

  const loadItems = useCallback(
    async (cursor?: string | null, append = false, search = q) => {
      const params = new URLSearchParams({ storeId, limit: '48', scope: 'all' });
      if (folder) params.set('folder', folder);
      if (search.trim()) params.set('q', search.trim());
      if (cursor) params.set('cursor', cursor);
      const res = await fetch(`${base}/gallery/items?${params}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'items_error');
      const list = (data.items ?? []) as GalleryItem[];
      setItems((prev) => (append ? [...prev, ...list] : list));
      setNextCursor(data.nextCursor ?? null);
    },
    [base, storeId, folder, q],
  );

  useEffect(() => {
    void (async () => {
      setBusy(true);
      setErr('');
      try {
        await loadFolders();
        await loadItems(null, false);
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
      } finally {
        setBusy(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [folder, storeId]);

  async function search(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await loadItems(null, false);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  }

  async function pick(item: GalleryItem) {
    setPicking(item.id);
    setErr('');
    try {
      await onPickArtwork(item);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setPicking(null);
    }
  }

  return (
    <div className="ox-gallery-browser">
      <form className="ox-gallery-filters" onSubmit={search}>
        <select value={folder} onChange={(e) => setFolder(e.target.value)} aria-label="Carpeta">
          <option value="">Todas las carpetas</option>
          {folders.map((f) => (
            <option key={f.slug} value={f.slug}>
              {f.label} ({f.slug})
            </option>
          ))}
        </select>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar nombre o código"
          aria-label="Buscar"
        />
        <button type="submit" disabled={busy}>
          Filtrar
        </button>
      </form>

      {err && <div className="ox-warn">{err}</div>}

      <div className="ox-gallery-grid">
        {items.map((it) => (
          <button
            key={it.id}
            type="button"
            className="ox-gallery-card"
            disabled={picking === it.id}
            onClick={() => void pick(it)}
            title={it.name}
          >
            <img
              src={it.artworkUrl}
              alt=""
              style={{
                background: it.garmentBias === 'light' ? '#eee' : '#222',
              }}
            />
            <span className="ox-gallery-card-name">{it.name}</span>
            <span className="ox-gallery-card-code">{it.code}</span>
          </button>
        ))}
      </div>

      {!items.length && !busy && !err && <p className="ox-muted">Sin resultados.</p>}

      {nextCursor && (
        <button
          type="button"
          className="ox-gallery-more"
          disabled={busy}
          onClick={() => {
            setBusy(true);
            void loadItems(nextCursor, true).finally(() => setBusy(false));
          }}
        >
          Cargar más
        </button>
      )}
    </div>
  );
}
